import Component from "@base/js/component";

export default class SubsiteMenu extends Component {
  /**
   * Constructor for the SubsiteMenu component
   * @param {HTMLElement} element - The root menu element
   */
  constructor(element) {
    super(element);
    this.element = element;
    this.isMobile = window.innerWidth < 768;
    this.toggleButtons = null;
    this.menuList = null;

    // Initialize the mobile menu
    this.setupMobileMenu();

    // Listen for window resize events
    this.handleResize = this.handleResize.bind(this);
    window.addEventListener("resize", this.handleResize);
  }

  /**
   * Handles the window resize event
   */
  handleResize() {
    const isMobile = window.innerWidth < 768;

    // Only act if the mobile state has changed
    if (isMobile !== this.isMobile) {
      this.isMobile = isMobile;
      this.cleanupMobileMenu();
      this.setupMobileMenu();
    }
  }

  /**
   * Cleans up the mobile menu's events and states
   */
  cleanupMobileMenu() {
    if (this.toggleButtons) {
      this.toggleButtons.forEach((button) => {
        // Clone the button to remove all event listeners
        const newButton = button.cloneNode(true);
        button.parentNode.replaceChild(newButton, button);
      });
    }

    // Reset menu visibility
    if (this.menuList) {
      const menuItems = this.menuList.querySelectorAll(
        ".subsite-menu__list-item"
      );
      menuItems.forEach((item) => {
        if (!item.classList.contains("is-active")) {
          item.classList.remove("is-visible");
        }
      });

      // Reset icon state
      this.toggleButtons = this.element.querySelectorAll(
        ".subsite-menu__icon-button"
      );
      this.toggleButtons.forEach((button) => {
        const icon = button.querySelector(".subsite-menu__icon");
        const useElement = icon.querySelector("use");
        useElement.setAttribute("xlink:href", "#add");
        icon.setAttribute("data-icon", "add");
        icon.classList.remove("icon--minus");
        icon.classList.add("icon--add");
      });
    }
  }

  /**
   * Sets up mobile menu interactivity
   * Handles menu toggle behavior on small screens
   */
  setupMobileMenu() {
    // Only activate on mobile devices
    if (this.isMobile) {
      this.toggleButtons = this.element.querySelectorAll(
        ".subsite-menu__icon-button"
      );
      this.menuList = document.querySelector(".subsite-menu__list");

      this.toggleButtons.forEach((button) => {
        // Add click event listener
        button.addEventListener("click", (e) => {
          e.preventDefault();

          const icon = button.querySelector(".subsite-menu__icon");
          const menuItems = this.menuList.querySelectorAll(
            ".subsite-menu__list-item"
          );
          const isExpanded = Array.from(menuItems).some((item) =>
            item.classList.contains("is-visible")
          );

          // Toggle visibility of menu items
          menuItems.forEach((item) => {
            // Skip the active item
            if (item.classList.contains("is-active")) return;

            item.classList.toggle("is-visible");
          });

          // Change the icon based on menu state
          const useElement = icon.querySelector("use");
          if (isExpanded) {
            // If expanded, change to '+'
            useElement.setAttribute("xlink:href", "#add");
            icon.setAttribute("data-icon", "add");
            icon.classList.remove("icon--minus");
            icon.classList.add("icon--add");
          } else {
            // If collapsed, change to '-'
            useElement.setAttribute("xlink:href", "#minus");
            icon.setAttribute("data-icon", "minus");
            icon.classList.remove("icon--add");
            icon.classList.add("icon--minus");
          }
        });

        // Add keyboard support for accessibility
        button.addEventListener("keydown", (e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            button.click();
          }
        });
      });
    } else {
      // If not mobile, ensure the menu is visible
      this.menuList = document.querySelector(".subsite-menu__list");
      const menuItems = this.menuList.querySelectorAll(
        ".subsite-menu__list-item"
      );
      menuItems.forEach((item) => {
        if (!item.classList.contains("is-active")) {
          item.classList.add("is-visible");
        }
      });
    }
  }

  /**
   * Static method to initialize SubsiteMenu on all matching elements
   */
  static init() {
    const subsiteMenuElements = document.querySelectorAll(".subsite-menu");
    subsiteMenuElements.forEach((element) => new SubsiteMenu(element));
  }
}

// Initialize the component when DOM is loaded
SubsiteMenu.init();
